import { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Filter, ArrowLeft } from 'lucide-react'
import { Container, Breadcrumb } from '@/components/layout'
import { ProductGrid, FilterSidebar } from '@/components/products'
import { useCategory } from '@/hooks/useCategories'
import { useProducts } from '@/hooks/useProducts'
import { useFilterStore } from '@/stores/filterStore'
import { stripHtml } from '@/lib/utils'
import type { FilterType } from '@/types'

export function CategoryPage() {
  const { category: categorySlug } = useParams<{ category: string }>()
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false)

  const { data: category, isLoading: categoryLoading } = useCategory(categorySlug)
  const { filters, clearFilters } = useFilterStore()

  // Get all products without filters to calculate available filter values
  const { data: allProducts } = useProducts({
    categorySlug,
  })

  // Get filtered products
  const { data: products, isLoading: productsLoading } = useProducts({
    categorySlug,
    filters,
  })

  // Calculate available filter values from all products in this category
  const availableFilterValues = useMemo(() => {
    const values: Record<FilterType, Set<string>> = {
      water_tightness: new Set(),
      fire_class: new Set(),
      material: new Set(),
      product: new Set(),
    }

    allProducts?.forEach(product => {
      product.filters?.forEach(filter => {
        if (filter.filter_type in values) {
          values[filter.filter_type as FilterType].add(filter.filter_value)
        }
      })
    })

    return {
      water_tightness: Array.from(values.water_tightness),
      fire_class: Array.from(values.fire_class),
      material: Array.from(values.material),
      product: Array.from(values.product),
    }
  }, [allProducts])

  // Clear filters when category changes
  useEffect(() => {
    clearFilters()
  }, [categorySlug, clearFilters])

  const breadcrumbItems = [
    { label: 'Products', href: '/products' },
    { label: category?.name || 'Loading...' },
  ]

  return (
    <>
      {/* Hero */}
      <section className="bg-secondary-900 text-white py-12 lg:py-16">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Breadcrumb items={breadcrumbItems} />

            <div className="mt-6">
              <Link
                to="/products"
                className="inline-flex items-center gap-2 text-secondary-400 hover:text-white mb-4 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                All Categories
              </Link>

              {categoryLoading ? (
                <div className="animate-pulse">
                  <div className="h-10 bg-secondary-700 rounded w-48 mb-4" />
                  <div className="h-5 bg-secondary-700 rounded w-96" />
                </div>
              ) : (
                <>
                  <h1 className="text-3xl lg:text-4xl font-bold mb-4">
                    {category?.name}
                  </h1>
                  {category?.description && (
                    <p className="text-lg text-secondary-300 max-w-2xl">
                      {stripHtml(category.description)}
                    </p>
                  )}
                </>
              )}
            </div>
          </motion.div>
        </Container>
      </section>

      {/* Products Section */}
      <section className="py-12 lg:py-16">
        <Container>
          {/* Mobile Filter Button - only show if filters are enabled */}
          {category?.show_filters !== false && (
            <div className="lg:hidden mb-6">
              <button
                onClick={() => setIsMobileFilterOpen(true)}
                className="btn-outline w-full justify-center"
              >
                <Filter className="w-5 h-5 mr-2" />
                Filters
              </button>
            </div>
          )}

          <div className="flex gap-8">
            {/* Sidebar - only show if filters are enabled for this category */}
            {category?.show_filters !== false && (
              <FilterSidebar
                isMobileOpen={isMobileFilterOpen}
                onMobileClose={() => setIsMobileFilterOpen(false)}
                availableFilterValues={availableFilterValues}
              />
            )}

            {/* Products */}
            <div className="flex-1 min-w-0">
              {/* Results Count */}
              <div className="flex items-center justify-between mb-6">
                <p className="text-secondary-600">
                  {productsLoading ? (
                    'Loading...'
                  ) : (
                    <>
                      <span className="font-semibold text-secondary-900">
                        {products?.length || 0}
                      </span>{' '}
                      products found
                    </>
                  )}
                </p>
              </div>

              <ProductGrid
                products={products || []}
                categorySlug={categorySlug}
                isLoading={productsLoading}
              />
            </div>
          </div>
        </Container>
      </section>
    </>
  )
}
